import { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';

import { BoxProps } from '@/shared/ui/box/box.types';
import { SortChangePayload, TanstackTableProps } from '@/shared/ui/table/table.types';
import { TableFilter, TableFiltersToggle } from '@/shared/ui/table/ui/table-filters/table-filters.types';

import { ValueOf } from '../interfaces/utils';

export const TABLE_MODE = {
	SERVER: 'server',
	CLIENT: 'client',
} as const;

export type TableMode = ValueOf<typeof TABLE_MODE>;

export type TableSearchConfig = {
	enabled?: boolean;
	placeholder?: string;
	debounceMs?: number;
};

export type TableSortingConfig = {
	enabled?: boolean;
	initial?: SortChangePayload;
};

export type TablePaginationConfig = {
	enabled?: boolean;
	initialPage?: number;
	initialPageSize?: number;
};

export type TableFilterParams<TFilters> = {
	mapToParams?: (filters?: TFilters) => Record<string, unknown>;
};

export type TableFilterConfig<TFilters> = TableFilterParams<TFilters> & {
	config?: TableFilter[];
	onChange?: (id: string, value: unknown) => void;
	containerProps?: BoxProps;
	wrapperProps?: BoxProps;
	fixedFilters?: string[];
	toggle?: TableFiltersToggle;
};

export type TableFeatures<TFilters> = {
	search?: TableSearchConfig;
	sorting?: TableSortingConfig;
	pagination?: TablePaginationConfig;
	filters?: TableFilterParams<TFilters>;
};

export type TableClientConfig<TRow, TFilters> = {
	filter?: (rows: TRow[], filters?: TFilters) => TRow[];
	search?: (row: TRow, query: string) => boolean;
	sort?: (rows: TRow[], sort: SortChangePayload) => TRow[];
};

export interface TableSchema<TRow, TFilters> extends TableFeatures<TFilters> {
	mode?: TableMode;
	columns: ColumnDef<TRow, string | number | boolean | ReactNode>[];
	getRowId?: (row: TRow) => string;
	tableProps?: Partial<
		Omit<
			TanstackTableProps<TRow>,
			| 'data'
			| 'columns'
			| 'isLoading'
			| 'page'
			| 'pageSize'
			| 'total'
			| 'totalPages'
			| 'onChangePage'
			| 'onPageSizeChange'
			| 'onChangeSort'
			| 'enableSorting'
			| 'sort'
			| 'manualSorting'
			| 'manualPagination'
			| 'filters'
			| 'showPagination'
		>
	>;
	filters?: TableFilterConfig<TFilters>;
	client?: TableClientConfig<TRow, TFilters>;
}
